import React, { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '@panel/commons';
import { useQuery } from '@panel/commons/hooks';
import { currencyOptionsBySite, site } from '@panel/commons/helpers';
import { WrapperContainer } from '../../Wrappers/WrapperContainer';
import { getPresentationInfo, updateAndPublish, Step4Module3Selectors } from '../../../ducks/step4Module3';
import { Step1Module1Selectors } from '../../../ducks/step1Module1';
import { sendEvent, PUBLICATION_ABANDON, track, trackMixpanel } from '../../../utils/trackingEvent';
import logoFsboDesktop from '../../../../assets/img/fsboLogoDesktop.svg';
import logoFsboMobile from '../../../../assets/img/fsboLogoMobile.svg';
import {
  Body,
  Title,
  SubTitle,
  ContactInfoContainer,
  ContactInfo,
  ContactInfoLogo,
  ContactInfoData,
  ContactInfoDataName,
  ContactInfoDataPhone,
  ContactInformationMessage,
  PreviewSection
} from './styles';
import { Preview } from './preview';
import { selectCurrentProduct } from '../../../ducks/step4Module2';

const planNames = Object.freeze({
  FREE: 'Gratis',
  SIMPLE: 'Simple',
  HIGHLIGHTED: 'Destacado',
  SUPERHIGHLIGHTED: 'Super destacado'
});
const siteId = site.id;

const Step4Module3 = WrapperContainer(({ setId }) => {
  const { t } = useTranslation();
  const [userContactInfo, setUserContactInfo] = useState({
    nameLastName: t('publisher.step4.module3.name.placeholder'),
    email: t('publisher.step4.module3.email.placeholder'),
    phone: '',
    whatsapp: t('publisher.step4.module3.whatsapp.placeholder')
  });
  const [planInfo, setPlanInfo] = useState({ selected: null, current: null, highlight: '', name: '' });
  const { contactInfo } = useSelector(Step4Module3Selectors.getPostingPreviewInfo);
  const { redirectFromAppraisal, originalValues } = useSelector(Step1Module1Selectors.getStep1Module1State);
  const { selectedPlan, currentPlan } = useSelector(Step4Module3Selectors.getPlanInfo);
  const { postingStatuses, operationTypeId } = useSelector(Step1Module1Selectors.getInfoEvents);
  const product = useSelector(selectCurrentProduct);
  const {
    propertySubTypes,
    propertyTypes,
    formValues: { selectedPropertyTypeId, selectedPropertySubTypeId }
  } = useSelector(Step1Module1Selectors.getStep1Module1State);
  const location = useLocation();
  const [postingId] = useQuery(location, ['postingId']);
  const [reachedBreakPoint] = useBreakpoint(768);
  const dispatch = useDispatch();
  const imageLogoFsbo = reachedBreakPoint ? logoFsboDesktop : logoFsboMobile;

  const propertyObj = { list: propertyTypes, id: selectedPropertyTypeId };
  const subPropertyObj = { list: propertySubTypes, id: selectedPropertySubTypeId };

  const notifyAbandon = () => {
    if (postingStatuses.includes('DRAFT')) {
      track(PUBLICATION_ABANDON, operationTypeId, postingId);
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', notifyAbandon);
    return () => {
      window.removeEventListener('beforeunload', notifyAbandon);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingStatuses]);

  useEffect(() => {
    setId('step4Module3Form');
    if (postingId) {
      dispatch(getPresentationInfo(postingId));
    }
  }, [dispatch, postingId, setId]);

  useEffect(() => {
    if (contactInfo) {
      const phones =
        contactInfo.phone.reduce((m, p) => {
          m[p.phone_type] = p;
          return m;
        }, {}) || {};
      setUserContactInfo({
        nameLastName: contactInfo.contact_name,
        email: contactInfo.emails[0],
        phone: phones.PHONE && phones.PHONE.phone,
        // eslint-disable-next-line no-nested-ternary
        whatsapp: phones.WHATSAPP
          ? phones.WHATSAPP.phone
          : phones.CELL_PHONE
          ? phones.CELL_PHONE.phone
          : t('publisher.step4.module3.whatsapp.placeholder')
      });
    }
  }, [contactInfo, t]);

  const getPlanId = useCallback(() => {
    const planCookie = Cookies.get(`selectedPlan${postingId}`);
    if (planCookie) {
      const [selected, current, highlight] = planCookie.split('_');
      setPlanInfo({ selected, current, highlight, name: planNames[highlight.toUpperCase()] });
    }
  }, [postingId]);

  useEffect(() => {
    if (selectedPlan !== null && currentPlan !== null) {
      setPlanInfo({
        selected: selectedPlan,
        current: currentPlan,
        highlight: selectedPlan.publication_areas[0].publication_type,
        name: planNames[selectedPlan.publication_areas[0].publication_type]
      });
    } else {
      getPlanId();
    }
  }, [selectedPlan, currentPlan, getPlanId]);

  const handlePublish = evt => {
    evt.preventDefault();
    const price = product.formatUnitPrice ? currencyOptionsBySite(siteId)[0].name + product.formatUnitPrice : undefined;
    const locationData = {
      ...location,
      search: null,
      state: {
        fromPreview: true
      }
    };
    if (redirectFromAppraisal) {
      trackMixpanel(
        'APPRAISAL_SUCCESFUL_PUBLICATION_EVENT',
        originalValues.selectedOperationTypeId,
        originalValues.selectedPropertyTypeId,
        postingId
      );
    }
    sendEvent('Planes Modulo3', 'Publicar', operationTypeId, propertyObj, subPropertyObj, product.name, price);
    dispatch(updateAndPublish(postingId, planInfo.selected, locationData));
  };
  return (
    <Body data-repa='step4module3-body'>
      <PreviewSection data-repa='step4module3-body-previewSection'>
        <Title data-repa='step4module3-body-title'>{t('publisher.step4.module3.preview.title')}</Title>
        <p>{t('publisher.step4.module3.preview.text')}</p>
        <Preview postingId={postingId} planInfo={planInfo} data-repa='step4module3-body-preview' />
      </PreviewSection>
      <Title data-repa='step4module3-body-preview-title'>{t('publisher.step4.module3.title')}</Title>
      <SubTitle data-repa='step4module3-body-previewSubtitle'>{t('publisher.step4.module3.text')}</SubTitle>
      <form data-repa='step4module3-body-preview-form' id='step4Module3Form' onSubmit={handlePublish}>
        <ContactInfoContainer data-repa='step4module3-body-preview-contactInfoContainer'>
          <ContactInfo>
            <ContactInfoLogo>
              <img src={imageLogoFsbo} alt='logoFsbo' />
            </ContactInfoLogo>
            <ContactInfoData>
              <ContactInfoDataName>{userContactInfo.nameLastName}</ContactInfoDataName>
              {
                <ContactInfoDataPhone mobile>
                  <p>{userContactInfo.whatsapp}</p>
                </ContactInfoDataPhone>
              }
              {userContactInfo.phone && (
                <ContactInfoDataPhone>
                  <p>{userContactInfo.phone}</p>
                </ContactInfoDataPhone>
              )}
            </ContactInfoData>
          </ContactInfo>
          <ContactInformationMessage>
            <p>{t('publisher.step4.module3.contactDetailsReminder')}</p>
          </ContactInformationMessage>
        </ContactInfoContainer>
      </form>
    </Body>
  );
});

export { Step4Module3 };
